import { bind } from 'bind-decorator';

import type { Constructable } from 'bz-utilities/ts';

import { CacheUpdateEvent } from 'tm3-core/events/cache';

export interface IEvent {}

export interface IMapEvent<T> extends IEvent {}
export interface ICrossTabEvent extends IEvent {
  __crossTabEvent: true;
}
export interface IMediatorPublishOptions {
  skipCrossTab?: boolean;
}

type THandler<T extends object> = (param: T) => void;
type TCrossTabPublisher = (event: ICrossTabEvent) => void;

export abstract class MediatorEvent {
  constructor(init?: Partial<MediatorEvent>) {
    Object.assign(this, init);
  }
}

export abstract class MediatorEventId<T = string> extends MediatorEvent {
  id!: T;

  constructor(init?: Partial<MediatorEventId<T>>) {
    super(init);
  }
}

export abstract class MediatorEventValue<V> extends MediatorEvent {
  value!: V;

  constructor(init?: Partial<MediatorEventValue<V>>) {
    super(init);
  }
}

export abstract class MediatorEventIdValue<V, I = string> extends MediatorEvent {
  id!: I;
  value!: V;

  constructor(init?: Partial<MediatorEventIdValue<V, I>>) {
    super(init);
  }
}

class Mediator {
  protected readonly _subs = new Map<Constructable<object>, Function[]>();
  protected _crossTabPublisher?: TCrossTabPublisher;

  @bind
  setCrossTabPublisher(publisher?: TCrossTabPublisher) {
    this._crossTabPublisher = publisher;
  }

  @bind
  updateCache() {
    return this.publish(new CacheUpdateEvent());
  }

  @bind
  publish<T extends object>(req: T, options?: IMediatorPublishOptions) {
    // fire and forget
    return new Promise<void>((resolve, reject) => {
      this._subs.get(req.constructor as Constructable<T>)?.forEach((fn) => fn(req));
      if (!options?.skipCrossTab && this.isCrossTabEvent(req)) {
        this._crossTabPublisher?.(req);
      }
      resolve();
    });
  }

  @bind
  private isCrossTabEvent(req: object): req is ICrossTabEvent {
    return (req as ICrossTabEvent).__crossTabEvent === true;
  }

  @bind
  map<R, T extends IMapEvent<R>>(req: T): Promise<R[]> {
    return this._subs.get(req.constructor as Constructable<T>)?.map((fn) => fn(req)) ?? [];
  }

  @bind
  subscribe<T extends object>(clazz: Constructable<T>, handler: THandler<T>) {
    const handlers = this._subs.get(clazz);
    if (!handlers) {
      this._subs.set(clazz, [handler]);
      return;
    }

    if (handlers.some((x) => x === handler)) {
      return;
    }

    handlers.push(handler);
  }

  @bind
  unsubscribe<T extends object>(clazz: Constructable<T>, handler: THandler<T>) {
    const handlers = this._subs.get(clazz);
    if (!handlers) {
      return;
    }

    handlers.splice(handlers.indexOf(handler), 1);
    if (handlers.length == 0) {
      this._subs.delete(clazz);
    }
  }
}

export default Mediator;
